import { HealerTrinketFightRow } from '../types';
import { formatDuration, formatNumber } from './utils';

export function healerWasPresentInFight(fight: HealerTrinketFightRow): boolean {
    if (fight.intellect !== null || fight.spirit !== null) {
        return true;
    }
    if (fight.castCount > 0) {
        return true;
    }
    return (
        fight.blueDragonEquipped || fight.eyeOfGruulEquipped || fight.earringEquipped || fight.mementoEquipped
    );
}

function mp5FromMana(mana: number, durationSeconds: number): number {
    if (durationSeconds <= 0) {
        return 0;
    }
    return (mana / durationSeconds) * 5;
}

function durationWeightedAverage(
    fights: HealerTrinketFightRow[],
    value: (fight: HealerTrinketFightRow) => number
): number {
    const totalDuration = fights.reduce((sum, fight) => sum + fight.durationSeconds, 0);
    if (totalDuration <= 0) {
        return 0;
    }
    return fights.reduce((sum, fight) => sum + value(fight) * fight.durationSeconds, 0) / totalDuration;
}

function meanOfNullable(
    fights: HealerTrinketFightRow[],
    getter: (fight: HealerTrinketFightRow) => number | null
): number | null {
    const values = fights.map(getter).filter((value): value is number => value !== null);
    if (values.length === 0) {
        return null;
    }
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function equippedCountLabel(
    fights: HealerTrinketFightRow[],
    isEquipped: (fight: HealerTrinketFightRow) => boolean
): string {
    const equipped = fights.filter(isEquipped).length;
    if (equipped === 0) {
        return '0';
    }
    if (equipped === fights.length) {
        return 'All';
    }
    return `${equipped}/${fights.length}`;
}

function formatObservedUptimeAggregate(
    fights: HealerTrinketFightRow[],
    isEquipped: (fight: HealerTrinketFightRow) => boolean,
    uptimeSeconds: (fight: HealerTrinketFightRow) => number,
    theoreticalPercent: (fight: HealerTrinketFightRow) => number
): string {
    const equippedFights = fights.filter(isEquipped);
    if (equippedFights.length > 0) {
        const duration = equippedFights.reduce((sum, fight) => sum + fight.durationSeconds, 0);
        const uptime = equippedFights.reduce((sum, fight) => sum + uptimeSeconds(fight), 0);
        const percent = duration > 0 ? (uptime / duration) * 100 : 0;
        return `${formatNumber(uptime, 1)}s (${formatNumber(percent, 1)}%)`;
    }
    const theoretical = durationWeightedAverage(fights, theoreticalPercent);
    return `th ${formatNumber(theoretical, 1)}%`;
}

function formatObservedMp5Aggregate(
    fights: HealerTrinketFightRow[],
    isEquipped: (fight: HealerTrinketFightRow) => boolean,
    mana: (fight: HealerTrinketFightRow) => number
): string {
    const equippedFights = fights.filter(isEquipped);
    if (equippedFights.length === 0) {
        return '—';
    }
    const duration = equippedFights.reduce((sum, fight) => sum + fight.durationSeconds, 0);
    const totalMana = equippedFights.reduce((sum, fight) => sum + mana(fight), 0);
    return formatNumber(mp5FromMana(totalMana, duration), 1);
}

function formatTheoreticalMp5Aggregate(
    fights: HealerTrinketFightRow[],
    theoreticalMp5: (fight: HealerTrinketFightRow) => number
): string {
    return formatNumber(durationWeightedAverage(fights, theoreticalMp5), 1);
}

export interface HealerTrinketBossAggregates {
    totalDurationSeconds: number;
    cpm: number;
    avgIntellect: number | null;
    avgSpirit: number | null;
    blueDragonEquipped: string;
    blueDragonUptime: string;
    blueDragonMp5: string;
    blueDragonTheoreticalMp5: string;
    eyeOfGruulEquipped: string;
    eyeOfGruulProcs: string;
    eyeOfGruulMp5: string;
    eyeOfGruulTheoreticalMp5: string;
    earringEquipped: string;
    earringUptime: string;
    earringMp5: string;
    earringTheoreticalMp5: string;
    mementoEquipped: string;
    mementoUptime: string;
    mementoMp5: string;
    mementoTheoreticalMp5: string;
}

export function aggregateHealerTrinketBossGroup(
    fights: HealerTrinketFightRow[],
    earringApplicable: boolean
): HealerTrinketBossAggregates {
    const totalDurationSeconds = fights.reduce((sum, fight) => sum + fight.durationSeconds, 0);
    const totalCasts = fights.reduce((sum, fight) => sum + fight.castCount, 0);
    const cpm = totalDurationSeconds > 0 ? (totalCasts / totalDurationSeconds) * 60 : 0;

    const eyeEquippedFights = fights.filter((fight) => fight.eyeOfGruulEquipped);
    const eyeProcs = eyeEquippedFights.reduce((sum, fight) => sum + fight.eyeOfGruulProcs, 0);
    const eyeConsumed = eyeEquippedFights.reduce((sum, fight) => sum + fight.eyeOfGruulConsumed, 0);
    const eyeTheoreticalProcs = fights.reduce((sum, fight) => sum + fight.eyeOfGruulTheoreticalProcs, 0);

    const aggregates: HealerTrinketBossAggregates = {
        totalDurationSeconds,
        cpm,
        avgIntellect: meanOfNullable(fights, (fight) => fight.intellect),
        avgSpirit: meanOfNullable(fights, (fight) => fight.spirit),
        blueDragonEquipped: equippedCountLabel(fights, (fight) => fight.blueDragonEquipped),
        blueDragonUptime: formatObservedUptimeAggregate(
            fights,
            (fight) => fight.blueDragonEquipped,
            (fight) => fight.blueDragonUptimeSeconds,
            (fight) => fight.blueDragonTheoreticalUptimePercent
        ),
        blueDragonMp5: formatObservedMp5Aggregate(
            fights,
            (fight) => fight.blueDragonEquipped,
            (fight) => fight.blueDragonMana
        ),
        blueDragonTheoreticalMp5: formatTheoreticalMp5Aggregate(fights, (fight) => fight.blueDragonTheoreticalMp5),
        eyeOfGruulEquipped: equippedCountLabel(fights, (fight) => fight.eyeOfGruulEquipped),
        eyeOfGruulProcs:
            eyeEquippedFights.length > 0 ? `${eyeConsumed}/${eyeProcs}` : `th ${formatNumber(eyeTheoreticalProcs, 1)}`,
        eyeOfGruulMp5: formatObservedMp5Aggregate(
            fights,
            (fight) => fight.eyeOfGruulEquipped,
            (fight) => fight.eyeOfGruulMana
        ),
        eyeOfGruulTheoreticalMp5: formatTheoreticalMp5Aggregate(fights, (fight) => fight.eyeOfGruulTheoreticalMp5),
        earringEquipped: earringApplicable ? equippedCountLabel(fights, (fight) => fight.earringEquipped) : '—',
        earringUptime: earringApplicable
            ? formatObservedUptimeAggregate(
                  fights,
                  (fight) => fight.earringEquipped,
                  (fight) => fight.earringUptimeSeconds,
                  (fight) => fight.earringTheoreticalUptimePercent
              )
            : '—',
        earringMp5: earringApplicable
            ? formatObservedMp5Aggregate(
                  fights,
                  (fight) => fight.earringEquipped,
                  (fight) => fight.earringMana
              )
            : '—',
        earringTheoreticalMp5: earringApplicable
            ? formatTheoreticalMp5Aggregate(fights, (fight) => fight.earringTheoreticalMp5)
            : '—',
        mementoEquipped: equippedCountLabel(fights, (fight) => fight.mementoEquipped),
        mementoUptime: formatObservedUptimeAggregate(
            fights,
            (fight) => fight.mementoEquipped,
            (fight) => fight.mementoUptimeSeconds,
            (fight) => fight.mementoTheoreticalUptimePercent
        ),
        mementoMp5: formatObservedMp5Aggregate(
            fights,
            (fight) => fight.mementoEquipped,
            (fight) => fight.mementoMana
        ),
        mementoTheoreticalMp5: formatTheoreticalMp5Aggregate(fights, (fight) => fight.mementoTheoreticalMp5),
    };

    return aggregates;
}

export function formatBossAggregateDuration(totalDurationSeconds: number): string {
    return formatDuration(totalDurationSeconds);
}
